import React, { useState } from "react";
import { ArrowUpRight, TrendingUp, FileDown, Pill, Users, Trophy, Truck, Activity, ListOrdered, Quote } from "lucide-react";
import { eur } from "../components/landing/landingUtils";

const URL_INFO = "https://farmaclass.es/informacion";
const URL_DOSSIER = "https://farmaclass.es/dossier-rentabilidad-farmaclass.pdf";
const MEJORA = 0.0901;
const MARGEN = 0.5;

const CIFRAS = [["13.504 €", "beneficio medio al año por farmacia"], ["+9,01 pts", "mejora media de cuota de genérico"], ["48.122 €", "al año, la mejor farmacia"], ["69", "farmacias trabajando ya con FarmaClass"]];
const INCLUYE = [
  [Activity, "Semáforo por molécula", "Cada conjunto homogéneo en verde, ámbar o rojo según tu grado de dispensación de genérico."],
  [ListOrdered, "Top 25 de pérdidas", "Las moléculas donde más dinero dejas en marca, con el importe recuperable mes a mes."],
  [Truck, "Mejor proveedor por molécula", "Con las condiciones de tus laboratorios te dice dónde comprar cada EFG para ganar más."],
  [TrendingUp, "Benchmark real", "Tu cuota de genérico frente a tu provincia, tu comunidad y el resto de España."],
  [Users, "Análisis por empleado", "Quién sustituye y quién no, con fichas individuales para trabajar el mostrador."],
  [Trophy, "Incentivos del equipo", "Cálculo de los incentivos según resultados para implicar a todo el equipo."],
];
const OPINIONES = [
  ["Llevamos poco tiempo utilizándolo y ya hemos notado una mejora importante en la rentabilidad de la venta de genéricos. Lo mejor es que hemos conseguido implicar y motivar a todo el equipo.", "Rego Lodos · Lugo"],
  ["Funciona fenomenal para controlar la compra de genéricos, mejorar el rendimiento de la farmacia y aumentar el beneficio. Una herramienta que toda farmacia debería tener.", "Farmacia Ldo. Juan L. Durán · Fuenlabrada"],
  ["Análisis real en la dispensación de genéricos frente a las marcas. Usamos tanto la comparativa como el incentivo para los trabajadores.", "Farmacia El Amparo · Icod de los Vinos"],
];

function Cta({ className = "", testid = "farmaclass-cta" }) {
  return (
    <a href={URL_INFO} target="_blank" rel="noopener noreferrer" data-testid={testid}
       className={"group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors shadow-[0_10px_40px_-12px_#10b981] " + className}>
      Ver FarmaClass <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </a>
  );
}

function Calculadora() {
  const [compra, setCompra] = useState(40000);
  const gana = compra * 12 * MEJORA * MARGEN;
  return (
    <div className="card p-7" data-testid="farmaclass-calc">
      <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Calcula el tuyo</div>
      <label className="block mt-3">
        <div className="flex justify-between items-baseline gap-3">
          <span className="text-sm font-semibold text-slate-700">¿Cuánto compras al mes en medicamento de receta?</span>
          <span className="font-display text-2xl font-extrabold text-slate-900" data-testid="farmaclass-calc-compra">{eur(compra)}</span>
        </div>
        <input type="range" min={10000} max={150000} step={1000} value={compra} onChange={(e) => setCompra(Number(e.target.value))}
               className="w-full mt-3 accent-emerald-500" data-testid="farmaclass-calc-slider" />
      </label>
      <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 p-5">
        <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Ganarías al año</div>
        <div className="font-display text-4xl font-extrabold text-emerald-700 mt-1 tabular-nums" data-testid="farmaclass-calc-result">{eur(gana)}</div>
      </div>
      <p className="text-[11px] text-slate-500 mt-3">Según FarmaClass: mejora media de 9,01 puntos de genérico y margen adicional medio del 50 % del genérico.</p>
    </div>
  );
}

export default function FarmaClass() {
  return (
    <div className="p-4 sm:p-8 fade-in max-w-7xl" data-testid="farmaclass-page">
      <section className="relative rounded-3xl overflow-hidden bg-[#06281f] text-white mb-10" data-testid="farmaclass-banner">
        <img src="https://farmaclass.es/farmaclass-hero.jpg" alt="FarmaClass · analítica de genéricos para farmacias" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06281f] via-[#06281f]/90 to-transparent" />
        <div className="relative p-8 sm:p-12 lg:p-16 max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-300">
            <Pill size={12} /> FarmaClass · de Archiduque Consulting
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-5 leading-[1.05]">
            ¿Quieres aumentar tu <span className="text-emerald-400">rentabilidad</span>?
          </h1>
          <p className="text-base md:text-lg text-emerald-50/90 mt-5 leading-relaxed">
            Farmanaliza te ayuda a liberar el stock parado. <b>FarmaClass</b> es su complemento: analiza tu dispensación, separa marca de genérico molécula a molécula y te dice dónde pierdes dinero y cómo recuperarlo.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Cta testid="farmaclass-banner-cta" />
            <a href={URL_DOSSIER} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-colors" data-testid="farmaclass-dossier">
              <FileDown size={16} /> Dossier de rentabilidad
            </a>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="farmaclass-cifras">
        {CIFRAS.map(([v, l]) => (
          <div key={l} className="card p-5">
            <div className="font-display text-3xl font-extrabold text-emerald-700">{v}</div>
            <div className="text-xs text-slate-600 mt-1">{l}</div>
          </div>
        ))}
        <p className="col-span-full text-[11px] text-slate-500 -mt-1">Datos publicados por FarmaClass sobre las farmacias que ya trabajan con su servicio.</p>
      </section>

      <section className="grid lg:grid-cols-[1.2fr_1fr] gap-8 mb-12 items-start">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">El dinero invisible</div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-2">Cada caja de marca que sale por la puerta tiene un coste que no ves.</h2>
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            {[["01", "Subes tu fichero de dispensación", "Excel, TXT o CSV de tu programa, también por empleado."], ["02", "FarmaClass lo clasifica", "Marca y genérico por molécula, cuota real en unidades y euros."], ["03", "Recibes el plan", "Top de pérdidas, pedidos al proveedor e informes PDF."]].map(([n, t, d]) => (
              <div key={n} className="border-l-2 border-emerald-500 pl-4">
                <div className="font-mono text-xs text-emerald-700 font-bold">{n}</div>
                <div className="font-semibold text-slate-900 mt-1">{t}</div>
                <div className="text-sm text-slate-600 mt-1">{d}</div>
              </div>
            ))}
          </div>
        </div>
        <Calculadora />
      </section>

      <section className="mb-12" data-testid="farmaclass-incluye">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Qué incluye FarmaClass</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {INCLUYE.map(([I, t, d]) => (
            <div key={t} className="card p-6 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow]">
              <span className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><I size={18} /></span>
              <div className="font-semibold text-slate-900 mt-4">{t}</div>
              <p className="text-sm text-slate-600 mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12" data-testid="farmaclass-opiniones">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Lo dicen las farmacias que ya lo usan</h2>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {OPINIONES.map(([q, a]) => (
            <figure key={a} className="card p-6 flex flex-col">
              <Quote size={20} className="text-emerald-500" />
              <blockquote className="text-sm text-slate-700 mt-3 leading-relaxed flex-1">“{q}”</blockquote>
              <figcaption className="text-xs font-semibold text-slate-500 mt-4">{a}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="rounded-3xl bg-[#06281f] text-white p-8 sm:p-12 flex flex-col md:flex-row gap-6 md:items-center justify-between" data-testid="farmaclass-final">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold">Pide tu análisis de genéricos sin compromiso</h2>
          <p className="text-emerald-50/80 mt-2">Te contactan en menos de 24 horas · 609 46 77 88 · pedro@archiduquecarlos.com</p>
        </div>
        <Cta testid="farmaclass-final-cta" />
      </section>
    </div>
  );
}
